import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Save, X, Package, Check, Pencil, Target } from 'lucide-react';
import TaskRow, { TaskSummary } from '@/components/TaskRow';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { getJson } from '@/lib/http';
import type { Task } from '@shared/schema';

const taskSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  isActive: z.boolean().default(true),
  taskDefinitionType: z.enum(['POSTING', 'REMOVAL']).default('POSTING'),
  parentTaskId: z.number().nullable().optional(),
});

type TaskFormData = z.infer<typeof taskSchema>;

interface TaskMediaRequirement {
  id: number;
  mediaFormatId: number;
  quantity: number;
  groupId?: number | null;
  groupLogic?: string | null;
  mediaFormat?: {
    id: number;
    name: string;
  } | null;
}

interface MediaFormat {
  id: number;
  name: string;
  type?: string | null;
  quantityType?: string | null;
}

interface Package {
  id: number;
  name: string;
}

// REMOVED: TaskCompositionDialog
// Tasks should NOT have media requirements editing - these come from pack_templates exclusively
// This component violated our hardened pack-based approach where:
// - Tasks define WORK (jobs to be done)  
// - Packs define MATERIALS (media formats via pack_templates)
// 
// Media requirements are now managed exclusively through pack templates

function TaskCompositionDialog_REMOVED({ taskId, onClose }: { taskId: number; onClose: () => void }) {
  const [selectedMediaFormat, setSelectedMediaFormat] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [logic, setLogic] = useState<'AND' | 'OR'>('AND');
  const [editingRequirement, setEditingRequirement] = useState<number | null>(null);
  const [editQuantity, setEditQuantity] = useState<number>(1);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const task = useQuery<Task>({
    queryKey: ['/api/tasks', taskId],
    queryFn: async () => {
      const response = await fetch(`/api/tasks/${taskId}`);
      if (!response.ok) throw new Error('Failed to fetch task');
      return response.json();
    },
    enabled: !!taskId,
  });

  const taskMediaRequirements = useQuery<TaskMediaRequirement[]>({
    queryKey: ['/api/tasks', taskId, 'media-requirements'],
    queryFn: async () => {
      const response = await fetch(`/api/tasks/${taskId}/media-requirements`);
      if (!response.ok) throw new Error('Failed to fetch requirements');
      return response.json();
    },
    enabled: !!taskId,
  });

  const mediaFormats = useQuery<MediaFormat[]>({
    queryKey: ['/api/media-formats'],
  });

  // Check if task is pack-linked
  const isPackLinkedQuery = useQuery<{ isPackLinked: boolean }>({
    queryKey: ['/api/tasks', taskId, 'is-pack-linked'],
    queryFn: async () => {
      const response = await fetch(`/api/tasks/${taskId}/is-pack-linked`);
      if (!response.ok) throw new Error('Failed to check pack linkage');
      return response.json();
    },
    enabled: !!taskId,
  });

  const isPackLinked = isPackLinkedQuery.data?.isPackLinked || false;

  console.log('Media formats in TaskCompositionDialog:', mediaFormats.data);
  console.log('Task pack-linked status:', { taskId, isPackLinked });

  const addRequirementMutation = useMutation({
    mutationFn: async (data: { mediaFormatId: number; quantity: number; groupId?: number; groupLogic?: string }) => {
      const response = await fetch(`/api/tasks/${taskId}/media-requirements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to add requirement');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/tasks', taskId, 'media-requirements'] });
      setSelectedMediaFormat('');
      setQuantity(1);
      toast({ title: 'Media format requirement added successfully' });
    },
    onError: () => {
      toast({ title: 'Failed to add media format requirement', variant: 'destructive' });
    },
  });

  const removeRequirementMutation = useMutation({
    mutationFn: async (requirementId: number) => {
      const response = await fetch(`/api/task-media-requirements/${requirementId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to remove requirement');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/tasks', taskId, 'media-requirements'] });
      toast({ title: 'Media format requirement removed successfully' });
    },
    onError: () => {
      toast({ title: 'Failed to remove media format requirement', variant: 'destructive' });
    },
  });

  const updateRequirementMutation = useMutation({
    mutationFn: async (data: { id: number; quantity: number; groupId?: number; groupLogic?: string }) => {
      const response = await fetch(`/api/task-media-requirements/${data.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          quantity: data.quantity, 
          groupId: data.groupId, 
          groupLogic: data.groupLogic 
        }),
      });
      if (!response.ok) throw new Error('Failed to update requirement');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/tasks', taskId, 'media-requirements'] });
      setEditingRequirement(null);
      toast({ title: 'Media format requirement updated successfully' });
    },
    onError: () => {
      toast({ title: 'Failed to update media format requirement', variant: 'destructive' });
    },
  });

  const handleAddRequirement = async () => {
    if (!selectedMediaFormat || quantity <= 0) return;
    
    if (logic === 'OR' && taskMediaRequirements.data && taskMediaRequirements.data.length > 0) {
      // Find the last requirement and group it with the new one
      const lastRequirement = taskMediaRequirements.data[taskMediaRequirements.data.length - 1];
      const groupId = Math.floor(Math.random() * 1000000); // Random ID within integer range
      
      // Update the last requirement to be part of the OR group
      await updateRequirementMutation.mutateAsync({
        id: lastRequirement.id,
        quantity: lastRequirement.quantity,
        groupId,
        groupLogic: 'OR',
      });
      
      // Add the new requirement to the same group
      addRequirementMutation.mutate({
        mediaFormatId: parseInt(selectedMediaFormat),
        quantity,
        groupId,
        groupLogic: 'OR',
      });
    } else {
      // Add as individual requirement
      addRequirementMutation.mutate({
        mediaFormatId: parseInt(selectedMediaFormat),
        quantity,
      });
    }
  };



  const startEditing = (requirement: TaskMediaRequirement) => {
    setEditingRequirement(requirement.id);
    setEditQuantity(requirement.quantity);
  };

  const convertToOrGroup = (requirement: TaskMediaRequirement) => {
    // This functionality is no longer needed with the simplified OR logic
    toast({ title: 'Use the "OR (Alternative)" option when adding new formats instead', variant: 'destructive' });
  };

  const handleUpdateRequirement = (requirementId: number) => {
    if (editQuantity <= 0) return;
    
    updateRequirementMutation.mutate({
      id: requirementId,
      quantity: editQuantity,
    });
  };

  const cancelEditing = () => {
    setEditingRequirement(null);
    setEditQuantity(1);
  };

  const availableFormats = mediaFormats.data?.filter(format => 
    !taskMediaRequirements.data?.some(req => req.mediaFormatId === format.id)
  ) || [];
  
  // For group builder, we need all formats available (not just unused ones)
  const allFormats = mediaFormats.data || [];

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl z-[60]" aria-describedby="task-composition-description">
        <DialogHeader>
          <DialogTitle>Edit Media Requirements - {task.data?.name}</DialogTitle>
          <div id="task-composition-description" className="text-sm text-muted-foreground">
            {isPackLinked 
              ? "This task is linked to a pack. Media requirements are managed at the pack level."
              : "Configure the media formats and quantities needed for this task"
            }
          </div>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Pack-linked read-only message */}
          {isPackLinked && (
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="py-6">
                <div className="text-center space-y-3">
                  <Package className="mx-auto h-12 w-12 text-orange-600" />
                  <div>
                    <h3 className="font-semibold text-orange-800">Pack-Linked Task</h3>
                    <p className="text-sm text-orange-700 mt-1">
                      This task is linked to a pack. Media requirements must be edited at the pack level, not individually for tasks.
                    </p>
                    <p className="text-sm text-orange-600 mt-2">
                      Go to Pack Management to modify the media requirements for this task.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Show normal editing interface only if NOT pack-linked */}
          {!isPackLinked && (
          <>
            {/* Current Requirements */}
            <div>
            <h3 className="font-semibold mb-3">Current Media Format Requirements</h3>
            {taskMediaRequirements.data?.length === 0 ? (
              <Card>
                <CardContent className="py-8">
                  <div className="text-center text-muted-foreground">
                    <Package className="mx-auto h-8 w-8 mb-2" />
                    <p>No media formats configured for this task</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {taskMediaRequirements.data?.map((requirement, index) => {
                  const format = mediaFormats.data?.find(f => f.id === requirement.mediaFormatId);
                  const isOrGroup = requirement.groupLogic === 'OR';
                  const prevRequirement = index > 0 ? taskMediaRequirements.data?.[index - 1] : null;
                  const isFirstInOrGroup = isOrGroup && (!prevRequirement || prevRequirement.groupId !== requirement.groupId);
                  const isLastInOrGroup = isOrGroup && (index === taskMediaRequirements.data!.length - 1 || 
                    taskMediaRequirements.data![index + 1].groupId !== requirement.groupId);

                  return (
                    <div key={requirement.id} className={`flex items-center justify-between p-3 border rounded-lg ${
                      isOrGroup ? 'bg-orange-50 border-orange-200' : 'bg-white'
                    }`}>
                      <div className="flex items-center gap-3">
                        {!isFirstInOrGroup && isOrGroup && (
                          <div className="text-sm font-medium text-orange-600 mr-2">OR</div>
                        )}
                        {editingRequirement === requirement.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={editQuantity}
                              onChange={(e) => setEditQuantity(parseInt(e.target.value) || 1)}
                              min="1"
                              className="w-16 px-2 py-1 text-sm border rounded"
                              autoFocus
                            />
                            <span className="text-sm text-muted-foreground">x</span>
                          </div>
                        ) : (
                          <Badge variant="outline">
                            {requirement.quantity}x
                          </Badge>
                        )}
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="font-medium">{format?.name || 'Unknown Format'}</div>
                            <div className="text-sm text-muted-foreground">
                              {format?.type} • {format?.quantityType}
                            </div>
                          </div>
                          {requirement.groupLogic === 'OR' ? (
                            <Badge variant="secondary" className="bg-orange-100 text-orange-700">
                              OR
                            </Badge>
                          ) : (
                            <Badge variant="success">
                              AND
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {editingRequirement === requirement.id ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUpdateRequirement(requirement.id)}
                              disabled={updateRequirementMutation.isPending}
                            >
                              <Check className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={cancelEditing}
                            >
                              <X className="h-4 w-4 text-gray-500" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => startEditing(requirement)}
                              className="hover:bg-blue-50"
                            >
                              <Pencil className="h-4 w-4 text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => convertToOrGroup(requirement)}
                              className="hover:bg-orange-50"
                              title="Convert to OR Group"
                            >
                              <Target className="h-4 w-4 text-orange-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeRequirementMutation.mutate(requirement.id)}
                              disabled={removeRequirementMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Add New Requirement */}
          <div>
            <h3 className="font-semibold mb-3">Add Media Format Requirement</h3>
            <div className="space-y-4">
              <div className="space-y-4">
                <div className="flex gap-4 items-end">
                  <div className="flex-1">
                    <label className="text-sm font-medium mb-2 block">Media Format</label>
                    <Select value={selectedMediaFormat} onValueChange={setSelectedMediaFormat}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a media format" />
                      </SelectTrigger>
                      <SelectContent className="z-[70]">
                        {availableFormats.length === 0 ? (
                          <div className="p-2 text-sm text-muted-foreground">
                            All media formats are already added to this task
                          </div>
                        ) : (
                          availableFormats.map((format) => (
                            <SelectItem key={format.id} value={format.id.toString()}>
                              {format.name} ({format.type})
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-24">
                    <label className="text-sm font-medium mb-2 block">Quantity</label>
                    <Input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                    />
                  </div>
                  <div className="w-32">
                    <label className="text-sm font-medium mb-2 block">Logic</label>
                    <Select value={logic} onValueChange={(value: 'AND' | 'OR') => setLogic(value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[70]">
                        <SelectItem value="AND">AND (Required)</SelectItem>
                        <SelectItem value="OR">OR (Alternative)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleAddRequirement}
                    disabled={!selectedMediaFormat || quantity <= 0 || addRequirementMutation.isPending}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Format
                  </Button>
                </div>
                
                {logic === 'OR' && (
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <p className="text-sm text-orange-800">
                      <strong>OR Logic:</strong> This format will be grouped with the previously added format as an alternative option.
                      Only ONE of the formats in the OR group needs to be fulfilled.
                    </p>
                  </div>
                )}
              </div>
              

            </div>
          </div>
          </>
          )}
        </div>

        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Done
          </Button>
        </div>
      </DialogContent>
      

    </Dialog>
  );
}

export default function TaskManagement() {
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch tasks with filtering moved into select for stable references
  const { data: tasksData, isLoading } = useQuery({
    queryKey: ['/api/admin/tasks'],
    queryFn: async () => {
      const response = await getJson<{ success: boolean; tasks: TaskSummary[] }>('/api/admin/tasks');
      return response.tasks || [];
    },
    select: (data): { active: TaskSummary[]; archived: TaskSummary[] } => ({
      active: data.filter(t => t.isActive !== false),
      archived: data.filter(t => t.isActive === false),
    }),
  });

  // Extract filtered tasks from select result
  const activeTasks = tasksData?.active ?? [];
  const archivedTasks = tasksData?.archived ?? [];
  const displayedTasks = showArchived ? archivedTasks : activeTasks;
  
  // Combined tasks for mutations that need to find task by id
  const allTasks = [...activeTasks, ...archivedTasks];

  // REMOVED: Campaign types, operator task rates, and media formats queries
  // Task management now only handles basic task name CRUD
  // Rates and assignments are managed via Task Rules page

  // REMOVED: getTaskMediaRequirements function
  // Tasks should NOT have media requirements - these come from pack_templates only

  const createTaskMutation = useMutation({
    mutationFn: (data: TaskFormData) => {
      // Send backend-required fields with defaults
      const payload = {
        ...data,
        defaultRate: 0.00,
        campaignTypeId: null,
        taskDefinitionType: data.taskDefinitionType || 'POSTING',
        parentTaskId: data.parentTaskId || null,
      };
      return apiRequest('/api/admin/tasks', 'POST', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/tasks'] });
      setIsCreateDialogOpen(false);
      toast({ title: 'Task created successfully' });
    },
    onError: () => {
      toast({ title: 'Failed to create task', variant: 'destructive' });
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: TaskFormData }) => {
      // Send backend-required fields with defaults
      const payload = {
        ...data,
        defaultRate: 0.00,
        campaignTypeId: null,
        taskDefinitionType: data.taskDefinitionType || 'POSTING',
        parentTaskId: data.parentTaskId || null,
      };
      return apiRequest(`/api/admin/tasks/${id}`, 'PUT', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/tasks'] });
      setEditingTaskId(null);
      toast({ title: 'Task updated successfully' });
    },
    onError: () => {
      toast({ title: 'Failed to update task', variant: 'destructive' });
    },
  });

  const archiveTaskMutation = useMutation({
    mutationFn: (id: number) => {
      const task = allTasks.find(t => t.id === id);
      return apiRequest(`/api/admin/tasks/${id}`, 'PUT', { 
        isActive: false,
        name: task?.name,
        description: task?.description,
        defaultRate: 0.00,
        campaignTypeId: null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/tasks'] });
      toast({ title: 'Task archived successfully' });
    },
    onError: (error) => {
      console.error('Archive task error:', error);
      toast({ title: 'Failed to archive task', variant: 'destructive' });
    },
  });

  const unarchiveTaskMutation = useMutation({
    mutationFn: (id: number) => {
      const task = allTasks.find(t => t.id === id);
      return apiRequest(`/api/admin/tasks/${id}`, 'PUT', { 
        isActive: true,
        name: task?.name,
        description: task?.description,
        defaultRate: 0.00,
        campaignTypeId: null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/tasks'] });
      toast({ title: 'Task restored successfully' });
    },
    onError: (error) => {
      console.error('Restore task error:', error);
      toast({ title: 'Failed to restore task', variant: 'destructive' });
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (id: number) => apiRequest(`/api/admin/tasks/${id}`, 'DELETE'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/tasks'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/operator-task-rates'] });
      toast({ title: 'Task deleted permanently' });
    },
    onError: (error) => {
      console.error('Delete task error:', error);
      toast({ title: 'Failed to delete task', variant: 'destructive' });
    },
  });

  const createForm = useForm<TaskFormData>({
    resolver: zodResolver(taskSchema),
    defaultValues: {
      name: '',
      description: '',
      isActive: true,
      taskDefinitionType: 'POSTING',
      parentTaskId: null,
    },
  });

  const editForm = useForm<TaskFormData>({
    resolver: zodResolver(taskSchema),
  });

  const handleCreateTask = (data: TaskFormData) => {
    createTaskMutation.mutate(data);
  };

  const handleUpdateTask = (data: TaskFormData) => {
    if (editingTaskId) {
      updateTaskMutation.mutate({ id: editingTaskId, data });
    }
  };

  // Stable handlers wrapped in useCallback for TaskRow memoization
  const handleArchiveTask = useCallback((id: number) => {
    if (confirm('Archive this task? It will be hidden but historical data will remain intact.')) {
      archiveTaskMutation.mutate(id);
    }
  }, [archiveTaskMutation]);

  const handleUnarchiveTask = useCallback((id: number) => {
    if (confirm('Restore this task? It will be visible and available for use again.')) {
      unarchiveTaskMutation.mutate(id);
    }
  }, [unarchiveTaskMutation]);

  const handleDeleteTask = useCallback((id: number) => {
    if (confirm('⚠️ PERMANENTLY DELETE this task? This will break historical references! Consider archiving instead.')) {
      deleteTaskMutation.mutate(id);
    }
  }, [deleteTaskMutation]);

  const startEditing = useCallback((task: TaskSummary) => {
    setEditingTaskId(task.id);
    editForm.reset({
      name: task.name,
      description: task.description || '',
      isActive: task.isActive !== undefined ? task.isActive : true,
      taskDefinitionType: (task as any).taskDefinitionType || 'POSTING',
      parentTaskId: (task as any).parentTaskId || null,
    });
  }, [editForm]);



  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Operator Tasks</h1>
          <p className="text-muted-foreground">
            Manage basic task definitions. Use the Task Rules page to configure operator-specific rates and payment rules.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="show-archived"
              checked={showArchived}
              onCheckedChange={setShowArchived}
            />
            <Label htmlFor="show-archived" className="cursor-pointer">
              Show Archived ({archivedTasks.length})
            </Label>
          </div>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Create Task Template
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Task</DialogTitle>
            </DialogHeader>
            <Form {...createForm}>
              <form onSubmit={createForm.handleSubmit(handleCreateTask)} className="space-y-4">
                <FormField
                  control={createForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Task Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., Poster Installation" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={createForm.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Optional description of the task" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={createForm.control}
                  name="taskDefinitionType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Task Type</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select task type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="POSTING">Posting Task</SelectItem>
                          <SelectItem value="REMOVAL">Removal Task</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {createForm.watch('taskDefinitionType') === 'REMOVAL' && (
                  <FormField
                    control={createForm.control}
                    name="parentTaskId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Parent Posting Task</FormLabel>
                        <Select 
                          onValueChange={(val) => field.onChange(val ? parseInt(val) : null)} 
                          value={field.value?.toString() || ''}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select parent posting task" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {activeTasks
                              .filter((t: any) => t.taskDefinitionType === 'POSTING' || !t.taskDefinitionType)
                              .map((t) => (
                                <SelectItem key={t.id} value={t.id.toString()}>
                                  {t.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <div className="text-sm text-muted-foreground">
                          Links this removal task to its parent posting task
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={createForm.control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Active</FormLabel>
                        <div className="text-sm text-muted-foreground">
                          Task is available for assignment to operators
                        </div>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createTaskMutation.isPending}>
                    {createTaskMutation.isPending ? 'Creating...' : 'Create Task'}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {displayedTasks.map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            isEditing={editingTaskId === task.id}
            showArchived={showArchived}
            onEdit={startEditing}
            onArchive={handleArchiveTask}
            onUnarchive={handleUnarchiveTask}
            onDelete={handleDeleteTask}
            isArchiving={archiveTaskMutation.isPending}
            isUnarchiving={unarchiveTaskMutation.isPending}
            isDeleting={deleteTaskMutation.isPending}
            editForm={
              editingTaskId === task.id ? (
                <Form {...editForm}>
                  <form onSubmit={editForm.handleSubmit(handleUpdateTask)} className="space-y-4">
                    <FormField
                      control={editForm.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input {...field} className="text-xl font-semibold" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={editForm.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Textarea {...field} placeholder="Task description" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={editForm.control}
                      name="taskDefinitionType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Task Type</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select task type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="POSTING">Posting Task</SelectItem>
                              <SelectItem value="REMOVAL">Removal Task</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {editForm.watch('taskDefinitionType') === 'REMOVAL' && (
                      <FormField
                        control={editForm.control}
                        name="parentTaskId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Parent Posting Task</FormLabel>
                            <Select 
                              onValueChange={(val) => field.onChange(val ? parseInt(val) : null)} 
                              value={field.value?.toString() || ''}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select parent posting task" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {activeTasks
                                  .filter((t: any) => t.taskDefinitionType === 'POSTING' || !t.taskDefinitionType)
                                  .map((t) => (
                                    <SelectItem key={t.id} value={t.id.toString()}>
                                      {t.name}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                            <div className="text-sm text-muted-foreground">
                              Links this removal task to its parent posting task
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    <FormField
                      control={editForm.control}
                      name="isActive"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                          <div className="space-y-0.5">
                            <FormLabel className="text-base">Active</FormLabel>
                            <div className="text-sm text-muted-foreground">
                              Task is available for assignment to operators
                            </div>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <div className="flex space-x-2">
                      <Button type="submit" size="sm" disabled={updateTaskMutation.isPending}>
                        <Save className="mr-2 h-4 w-4" />
                        {updateTaskMutation.isPending ? 'Saving...' : 'Save'}
                      </Button>
                      <Button type="button" size="sm" variant="outline" onClick={() => setEditingTaskId(null)}>
                        <X className="mr-2 h-4 w-4" />
                        Cancel
                      </Button>
                    </div>
                  </form>
                </Form>
              ) : null
            }
          />
        ))}
      </div>

      {displayedTasks.length === 0 && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center space-y-4">
              <div className="text-muted-foreground">
                <Plus className="mx-auto h-12 w-12 mb-4" />
                <h3 className="text-lg font-semibold">
                  {showArchived ? 'No archived tasks' : 'No task templates configured'}
                </h3>
                <p>
                  {showArchived 
                    ? 'All tasks are active' 
                    : 'Create your first task template to define job types for operators'}
                </p>
              </div>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create First Task Template
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
}
